import {Button, IconButton, TagGroup} from '@wandb/components';
import {
  getTooltipForRunState,
  RunState,
} from '@wandb/weave/components/RunState';
import {getTagContrastColor} from '@wandb/weave/components/Tag/utils';
import React, {useMemo} from 'react';
import {useParams} from 'react-router-dom';

import {AGENT_NAME, useWBAgentController} from '../../../aria';
import {RunActionsDropdown} from '../../../pages/Run/RunActionsDropdown';
import {RunOverflowMenu} from '../../../pages/Run/RunOverflowMenu';
import {usePartMaybe} from '../../../state/views/hooks';
import {runColor} from '../../../util/colors';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import {getCustomRunName} from '../../../util/runs';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useWorkspaceSettingsColorPalette} from '../../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {useSemanticLegendSettingsContext} from '../../WorkspaceDrawer/Settings/semanticLegend/SemanticLegendSettingsWithCategoricalConfigContextProvider';
import {useSingleRunBackUrl} from './useSingleRunBackUrl';

export const SingleRunHeader = () => {
  const {run} = useSingleRunModeContext();
  const backUrl = useSingleRunBackUrl();
  const {customRunColorsRef, customRunNamesRef} = useWorkspaceRefsContext();
  const customRunNames = usePartMaybe(customRunNamesRef);
  const customRunColors = usePartMaybe(customRunColorsRef);
  const {semanticLegendSettings, semanticLegendRunColorOptions} =
    useSemanticLegendSettingsContext();
  const colorPaletteOption = useWorkspaceSettingsColorPalette();
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const {isAvailable: isAgentChatAvailable, startChat} = useWBAgentController();

  const tags = useMemo(() => {
    return (
      run?.tags.map(tag => ({
        id: tag.id,
        textValue: tag.name,
        color: getTagContrastColor(tag.colorIndex),
        removable: false,
      })) ?? []
    );
  }, [run]);

  if (!run) {
    return null;
  }

  const runDisplayColor = runColor({
    run,
    customRunColors,
    semanticLegendSettings: {
      ...semanticLegendSettings,
      runColorOptions: semanticLegendRunColorOptions,
    },
    colorPaletteOption,
  });
  const customRunName = getCustomRunName(run.name, false, customRunNames);

  const debugRun = () => {
    if (!entityName || !projectName) {
      return;
    }
    startChat(
      [
        {
          type: 'text',
          text: `Debug this run and explain why it ${run.state}:`,
          status: 'complete',
        },
        {
          type: 'wandb-run',
          entityName,
          projectName,
          runName: run.name,
          runDisplayName: run.displayName,
        },
      ],
      {delivery: 'send', target: 'new'}
    );
  };

  return (
    <div
      className={'p-16 border-b border-b-wb-border md:pb-0 md:border-b-0'}
      data-dd-privacy={DD_PRIVACY_ALLOW}>
      <header className="grid grid-cols-[auto_auto_minmax(80px,1fr)_auto_minmax(0,max-content)] items-center gap-x-12 gap-y-4 md:grid-cols-[auto_auto_minmax(0,1fr)_minmax(0,max-content)]">
        <IconButton
          icon="back"
          tooltip={{children: 'Multi-run view'}}
          aria-label="Exit single-run mode"
          data-dd-action-name="Exit single run mode"
          href={backUrl}
        />
        <div
          className="h-16 w-16 shrink-0 rounded-full"
          style={{backgroundColor: runDisplayColor}} // cannot use tailwind for dynamic values
        />
        <h2
          data-dd-action-name="Run name"
          className="min-w-0 truncate md:overflow-visible md:whitespace-normal md:text-clip md:break-all text-lg font-semibold">
          {customRunName ? (
            <>
              <span>{customRunName} </span>
              <span className="text-sm text-wb-text-tertiary">
                ({run.displayName})
              </span>
            </>
          ) : (
            run.displayName
          )}
          <span className="ml-8 hidden align-middle md:inline-block">
            <RunState
              value={run.state}
              tooltip={getTooltipForRunState(run.state)}
            />
          </span>
        </h2>
        <div className="md:hidden">
          <RunState
            value={run.state}
            tooltip={getTooltipForRunState(run.state)}
          />
        </div>
        <div className="flex items-center gap-8 overflow-x-auto [scrollbar-width:thin] [scrollbar-color:#0d0f1252_transparent] [&>*]:shrink-0">
          {(isAgentChatAvailable && run.state === 'failed') ||
          run.state === 'crashed' ? (
            <Button
              startIcon="lightboard-solid"
              onPress={debugRun}>{`Debug with ${AGENT_NAME}`}</Button>
          ) : null}
          <RunActionsDropdown
            entityName={entityName}
            projectName={projectName}
            runName={run.name}
          />
          <RunOverflowMenu
            entityName={entityName}
            projectName={projectName}
            runName={run.name}
            customRunName={customRunName}
          />
        </div>
        {run.tags.length > 0 && (
          <div className="col-start-3 col-end-6 md:col-end-5">
            <TagGroup aria-label="Run tags" items={tags} />
          </div>
        )}
      </header>
    </div>
  );
};
